/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { trackPageView } from './utils/analytics';
import Header from './components/Header';
import Hero from './components/Hero';
import ProductGrid from './components/ProductGrid';
import BackToTop from './components/BackToTop';
import AuthModal from './components/AuthModal';
import SeasonalCalendar from './components/SeasonalCalendar';
import { useAuth } from './components/AuthContext';
import { Leaf, Instagram, Facebook, Mail, MessageSquare, Loader2 } from 'lucide-react';

// Sporedne stranice se učitavaju tek kad korisnik ode na njih — manji početni bundle.
const SellersPage = lazy(() => import('./components/SellersPage'));
const MyAdsPage = lazy(() => import('./components/MyAdsPage'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const HowToSell = lazy(() => import('./components/HowToSell'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const FAQ = lazy(() => import('./components/FAQ'));
const ContactsPage = lazy(() => import('./components/ContactsPage'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const RequireAdmin = lazy(() => import('./components/RequireAdmin'));

const SEARCH_URL_SYNC_MS = 400;

function PageLoader() {
  return (
    <div className="pt-40 pb-32 flex items-center justify-center gap-3 text-sage-600">
      <Loader2 className="animate-spin" size={22} />
      <span className="text-sm font-bold">Učitavanje...</span>
    </div>
  );
}

function Footer() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const goTo = useCallback((path) => {
    navigate(path);
    window.scrollTo(0, 0);
  }, [navigate]);
  return (
    <footer className="bg-sage-800 text-white pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-12 mb-16">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-6 text-white">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Leaf size={24} />
              </div>
              <span className="text-2xl font-serif font-bold tracking-tight">Eko Pijaca</span>
            </div>
            <p className="text-sage-100/60 leading-relaxed">
              Vaše omiljeno mesto za kupovinu i prodaju domaće hrane. Podržite domaću poljoprivredu.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-6">Korisni linkovi</h4>
            <ul className="space-y-4 text-sage-100/60 font-medium">
              <li><button onClick={() => goTo('/prodavci')} className="hover:text-white transition-colors">Prodavci</button></li>
              <li><button onClick={() => goTo('/o-nama')} className="hover:text-white transition-colors">O nama</button></li>
              <li><button onClick={() => goTo('/kako-prodati')} className="hover:text-white transition-colors">Kako prodati?</button></li>
              <li><button onClick={() => goTo('/pravila')} className="hover:text-white transition-colors">Pravila korišćenja</button></li>
              <li><button onClick={() => goTo('/cesta-pitanja')} className="hover:text-white transition-colors">Česta pitanja</button></li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-6">Kontakt</h4>
            <ul className="space-y-4 text-sage-100/60">
              <li className="flex items-center gap-3">
                <Mail size={18} />
                <span>podrska@ekopijaca.rs</span>
              </li>
              {user?.isAdmin && (
                <li>
                  <button
                    onClick={() => goTo('/poruke')}
                    className="inline-flex items-center gap-2 text-sage-100/80 hover:text-white transition-colors font-medium"
                  >
                    <MessageSquare size={16} />
                    Sve poruke
                  </button>
                </li>
              )}
              <li className="pt-4">
                <a
                  href="https://www.instagram.com/ekopijaca.rs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sage-100/80 hover:text-white transition-colors font-medium"
                >
                  <Instagram size={20} />
                  ekopijaca.rs
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/profile.php?id=61590287035652"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sage-100/80 hover:text-white transition-colors font-medium"
                >
                  <Facebook size={20} />
                  Eko Pijaca
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-10 text-center text-sage-100/30 text-sm">
          <p>© {new Date().getFullYear()} Eko Pijaca. Sva prava zadržana. Dizajnirano za domaće proizvođače.</p>
        </div>
      </div>
    </footer>
  );
}

function RedirectToPonuda() {
  const location = useLocation();
  return <Navigate to={`/ponuda${location.search}${location.hash}`} replace />;
}

function HomePage({ searchQuery, setSearchQuery, onBecomeSeller }) {
  const navigate = useNavigate();

  const handleSeasonalPick = (name) => {
    setSearchQuery(name);
    navigate(`/ponuda?q=${encodeURIComponent(name)}`, { replace: false });
    requestAnimationFrame(() => {
      const el = document.getElementById('ponuda');
      el?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  return (
    <>
      <Hero
        onExplore={() => {
          const el = document.getElementById('ponuda');
          el?.scrollIntoView({ behavior: 'smooth' });
        }}
        onBecomeSeller={onBecomeSeller}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <SeasonalCalendar onPickItem={handleSeasonalPick} />
      </div>
      <ProductGrid globalSearchQuery={searchQuery} onResetSearch={() => setSearchQuery('')} />
    </>
  );
}

export default function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authInitialStep, setAuthInitialStep] = useState('login');

  const openAuth = (initialStep = 'login') => {
    setAuthInitialStep(initialStep);
    setIsAuthOpen(true);
  };
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Sinhronizacija URL ?q= sa search query state-om.
  // Omogućava da deljen link tipa /ponuda?q=jagode automatski filtrira.
  useEffect(() => {
    if (location.pathname !== '/ponuda') return;
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    setSearchQuery((current) => (current !== q ? q : current));
  }, [location.pathname, location.search]);

  // Obrnuti smer: kucanje u pretragu upisuje ?q= u URL (debounced, replace)
  // pa je trenutna pretraga uvek deljiva linkom.
  useEffect(() => {
    if (location.pathname !== '/ponuda') return;
    const t = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const current = params.get('q') || '';
      if (current === searchQuery) return;
      if (searchQuery) params.set('q', searchQuery);
      else params.delete('q');
      navigate({ pathname: '/ponuda', search: params.toString() }, { replace: true });
    }, SEARCH_URL_SYNC_MS);
    return () => clearTimeout(t);
  }, [searchQuery, location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-earth-50">
      <Header
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onOpenAuth={() => openAuth('login')}
      />
      <main>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<RedirectToPonuda />} />
            <Route
              path="/ponuda"
              element={
                <HomePage
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onBecomeSeller={() => openAuth('register')}
                />
              }
            />
            <Route path="/prodavci" element={<SellersPage />} />
            <Route path="/moji-oglasi" element={<MyAdsPage />} />
            <Route path="/o-nama" element={<AboutUs />} />
            <Route path="/kako-prodati" element={<HowToSell onBecomeSeller={() => openAuth('register')} />} />
            <Route path="/pravila" element={<TermsOfService />} />
            <Route path="/cesta-pitanja" element={<FAQ />} />
            <Route path="/poruke" element={<RequireAdmin><ContactsPage /></RequireAdmin>} />
            <Route path="/reset-lozinke" element={<ResetPasswordPage />} />
            <Route path="*" element={<Navigate to="/ponuda" replace />} />
          </Routes>
        </Suspense>
      </main>
      <BackToTop />
      <Footer />

      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        initialStep={authInitialStep}
      />
    </div>
  );
}
